import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Dialog} from 'primereact/dialog'
import {forwardRef, useCallback, useImperativeHandle, useMemo} from 'react'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout, getComposedDataList} from '../form/Constant'
import {useListUserWithRoles} from '../healthcare/query-keys/HealthCareQueryManager'
import {useListHospitals} from '../hospital/query-keys/HospitalQueryManager'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {supportTeamList} from './SupportTeamsConstants'
import {useSupportTeamCRUD} from './query-keys/SupportTeamKeysManager'

const HospitalOptionsFilterRequest = {}
const HealthCareOptionsFilterRequest = {systemRole: SystemRole.USER_WITH_ROLE}

const SupportTeamForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    //#region  Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    //#endregion

    //#region Translate
    const {translateDatatable} = TranslateUtils()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const data = useSignal({})
    const hospitalValueSelected = useSignal(null)
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const hcpParticipantFilterRequest = useMemo(
        () => ({
            ...HealthCareOptionsFilterRequest,
            hospitalId: hospitalValueSelected.value,
        }),
        [hospitalValueSelected.value],
    )

    const {data: hospitalOptions} = useListHospitals(
        user?.id,
        true,
        HospitalOptionsFilterRequest,
        !!user?.id,
    )

    const {data: hcpOptions} = useListUserWithRoles(
        user?.id,
        true,
        null,
        hcpParticipantFilterRequest,
        !!user?.id,
    )

    const {mutateAsync: createOrUpdateSupportTeam} = useSupportTeamCRUD(
        !!data?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Memo
    const composedDataList = useMemo(() => {
        return getComposedDataList(supportTeamList, data.value)
    }, [data.value])

    const handleOnChangeHospitalSelected = useCallback(
        value => {
            hospitalValueSelected.value = value
        },
        [hospitalValueSelected],
    )

    const dataList = useMemo(() => {
        let tmp = [...composedDataList]
        tmp?.forEach((part, index, theArray) => {
            if (part?.key === 'participants') {
                if (theArray[index]?.children?.length > 0) {
                    for (let i = 0; i < theArray[index].children.length; i++) {
                        if (
                            theArray[index].children[i].key ===
                            'selectedParticipants'
                        ) {
                            theArray[index].children[i].initialValue = data
                                .value?.participantIds
                                ? data.value.participantIds
                                : []
                            theArray[index].children[i].options = hcpOptions
                                ? hcpOptions
                                : []
                        }
                    }
                }
            } else {
                if (theArray[index]?.children?.length > 0) {
                    let hospitalList =
                        hospitalOptions && hospitalOptions?.length > 0
                            ? [...hospitalOptions]
                            : []

                    for (let i = 0; i < theArray[index].children.length; i++) {
                        if (theArray[index].children[i].key === 'hospital') {
                            if (userRole === SystemRole.ADMIN) {
                                theArray[index].children[i].options =
                                    hospitalList ? hospitalList : []
                                theArray[index].children[i].initialValue = data
                                    .value?.hospitalId
                                    ? data.value?.hospitalId
                                    : []
                                theArray[index].children[i].callBack =
                                    handleOnChangeHospitalSelected
                            } else {
                                theArray[index].children.splice(i, 1)
                            }
                        }
                    }
                }
            }
        })

        return tmp
    }, [
        composedDataList,
        data.value.participantIds,
        data.value?.hospitalId,
        hcpOptions,
        hospitalOptions,
        userRole,
        handleOnChangeHospitalSelected,
    ])
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()
        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}

        /* Set hospital ID */
        let hospitalId = null

        if (userRole === SystemRole.USER_WITH_ROLE) {
            hospitalId = currentHospital?.id
        } else {
            hospitalId = value?.hospitalId
        }
        hospitalValueSelected.value = hospitalId || null

        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async value => {
        let isEmptyName = checkEmptyString(value?.name)
        if (!!isEmptyName) {
            showNotification(severityNotification.warn, 'Name is required !')
            return
        }

        let hospitalId = null
        if (userRole === SystemRole.ADMIN) {
            if (!value?.hospital || value?.hospital?.length < 1) {
                showNotification(
                    severityNotification.error,
                    'Hospitals are required !',
                )
                return
            }

            hospitalId = value?.hospital
        } else {
            hospitalId = currentHospital?.id
        }

        if (
            !value?.selectedParticipants ||
            value?.selectedParticipants?.length < 1
        ) {
            showNotification(
                severityNotification.error,
                'Participants are required !',
            )
            return
        }

        let toPostData = {
            ...data.value,
            ...value,
            userIds: value?.selectedParticipants,
            hospitalId: hospitalId || null,
        }

        const supportTeamData = await createOrUpdateSupportTeam(toPostData)

        if (!!supportTeamData?.id) {
            const {supportTeamKeys} = QueryKeyManager(
                QueryKeyTypes.supportTeam,
                user?.id,
            )

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: supportTeamKeys.pattern(),
            })

            // try {
            //     reloadDataTable(QueryKey, supportTeam)
            // } catch (err) {
            //     console.log('Fail to reload table')
            // }

            handleHideForm()
        }
    }
    //#endregion

    return (
        <Dialog
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            header={translateDatatable('SUPPORT TEAM FORM')}
            blockScroll
            fullScreen>
            <PageContentWrapper>
                <CustomForm
                    dataList={dataList}
                    layout={formLayout.group}
                    onSubmit={handleOnSubmit}
                />
            </PageContentWrapper>
        </Dialog>
    )
})

export default SupportTeamForm
